<template>
  <div class="solution-details-page">
    <Header />
    
    <div class="solution-details-container">
      <div class="page-header">
        <h1 class="page-title">Solution Details</h1>
        <p class="page-subtitle">Detailed information about your selected solution and matching providers</p>
      </div>
      <!-- Navigation Breadcrumbs -->
      <div class="breadcrumbs">
        <button class="breadcrumb-btn" @click="goBack">
          <i class="bi bi-arrow-left"></i> Back to Solutions
        </button>
      </div>
      <!-- Outer Card Container (wie in Solutions.vue) -->
      <div class="outer-card-container">
        <!-- Main Content Layout -->
        <div class="content-layout">
          <!-- 1) Input Summary Card (moved to top and collapsible) -->
          <div class="detail-card input-data-card">
            <div class="card-header cursor-pointer" @click="toggleCompanyInfo">
              <h2 class="card-title">
                <i class="bi bi-info-circle"></i>
                Company & Surplus Information
              </h2>
              <div class="collapse-toggle">
                <i :class="showCompanyInfo ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
              </div>
            </div>
            
            <div class="card-body" v-if="showCompanyInfo">
              <!-- Two-column layout for larger screens -->
              <div class="two-column-grid">
                <!-- Company Information -->
                <div class="info-section">
                  <h3 class="section-title">Company Information</h3>
                  <div class="info-grid">
                    <div class="info-item" v-if="companyProfile.name">
                      <span class="info-label">Company Name</span>
                      <span class="info-value">{{ companyProfile.name }}</span>
                    </div>
                    <div class="info-item" v-if="companyProfile.businessCategory">
                      <span class="info-label">Business Category</span>
                      <span class="info-value">{{ companyProfile.businessCategory }}</span>
                    </div>
                    <div class="info-item full-width" v-if="companyProfile.naceCodes && companyProfile.naceCodes.length > 0">
                      <span class="info-label">NACE Code(s)</span>
                      <span class="info-value">{{ companyProfile.naceCodes.map(obj => obj.code).join(', ') }}</span>
                    </div>
                    <div class="info-item full-width" v-if="companyProfile.description">
                      <span class="info-label">Description</span>
                      <span class="info-value">{{ companyProfile.description }}</span>
                    </div>
                    <div class="info-item full-width" v-if="hasLocationInfo(companyProfile.location)">
                      <span class="info-label">Location</span>
                      <span class="info-value">{{ formatAddress(companyProfile.location) }}</span>
                    </div>
                  </div>
                </div>
                <!-- Surplus Information -->
                <div class="info-section">
                  <h3 class="section-title">Surplus Information</h3>
                  <div class="info-grid">
                    <div class="info-item" v-if="wasteInfo.surplusName">
                      <span class="info-label">Surplus Name</span>
                      <span class="info-value">{{ wasteInfo.surplusName }}</span>
                    </div>
                    <div class="info-item" v-if="wasteInfo.amount && wasteInfo.amount.value">
                      <span class="info-label">Amount</span>
                      <span class="info-value">{{ wasteInfo.amount.value }} {{ wasteInfo.amount.unit }}</span>
                    </div>
                    <div class="info-item" v-if="wasteInfo.frequency && wasteInfo.frequency.times">
                      <span class="info-label">Frequency</span>
                      <span class="info-value">{{ frequencyString }}</span>
                    </div>
                    <div class="info-item full-width" v-if="wasteInfo.foodGroups && wasteInfo.foodGroups.length > 0">
                      <span class="info-label">Food Groups</span>
                      <div class="tags-container">
                        <span class="tag" v-for="(group, idx) in wasteInfo.foodGroups" :key="`group-${idx}`">
                          {{ group }}
                        </span>
                      </div>
                    </div>
                    <div class="info-item full-width" v-if="wasteInfo.foodSubgroups && wasteInfo.foodSubgroups.length > 0">
                      <span class="info-label">Food Subgroups</span>
                      <div class="tags-container">
                        <span class="tag" v-for="(subgroup, idx) in wasteInfo.foodSubgroups" :key="`subgroup-${idx}`">
                          {{ subgroup }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <!-- Seasonal Information (Only if seasonal is true) -->
                  <div v-if="wasteInfo.seasonal" class="seasonal-info">
                    <h4 class="subsection-title">
                      <i class="bi bi-calendar3"></i> Seasonal Capacity
                    </h4>
                    <div class="monthly-capacity-grid">
                      <div 
                        v-for="month in wasteInfo.monthlyCapacities.filter(m => m && m.active)" 
                        :key="month.name"
                        class="month-item"
                      >
                        <span class="month-name">{{ month.name }}</span>
                        <span class="month-value">{{ month.capacity }} {{ wasteInfo.amount.unit }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- 2) Solution Information Card (moved down) -->
          <div class="detail-card solution-info-card">
            <div class="card-header">
              <h2 class="card-title">
                <i class="bi bi-lightbulb"></i>
                Solution Information
              </h2>
              
              <!-- Neues schlichteres Design für Match Score -->
              <div class="match-score-container" v-if="solutionScore !== null">
                <div class="match-score-label">Match Score</div>
                <div class="match-score-wrapper">
                  <div class="match-score-bar">
                    <div 
                      class="match-score-fill" 
                      :class="getScoreClass(solutionScore)"
                      :style="{ width: `${solutionScore}%` }"
                    ></div>
                  </div>
                  <div class="match-score-value" :class="getScoreClass(solutionScore)">
                    {{ solutionScore }}
                  </div>
                </div>
                <div class="match-score-text">{{ getScoreLabel(solutionScore) }}</div>
              </div>
            </div>
            <div class="card-body">
              <!-- Toggle: FoodCycle vs. VCG.ai (hidden from user but kept for internal logic) -->
              <template v-if="solution.solution_origin === 'vcg.ai'">
                <VcgSolutionDetails :vcgSolution="solution" />
              </template>
              <template v-else>
                <FoodcycleSolutionDetails :fcSolution="solution" />
              </template>
              
              <!-- Matching Reasoning Sektion -->
              <div v-if="solutionReasoning" class="matching-reasoning-section">
                <div class="reasoning-header">
                  <i class="bi bi-list-check"></i>
                  <h3 class="reasoning-title">Why This Solution Matches</h3>
                </div>
                <div class="reasoning-content">
                  <p>{{ solutionReasoning }}</p>
                </div>
              </div>
            </div>
          </div>
          <!-- 3) Provider Companies Card -->
          <div class="detail-card provider-card">
            <div class="card-header">
              <h2 class="card-title">
                <i class="bi bi-building"></i>
                Provider Companies
              </h2>
              <div v-if="isLoadingProviders" class="loading-indicator">
                <div class="spinner"></div>
                Loading providers...
              </div>
            </div>
            <div class="card-body">
              <button 
                class="toggle-providers-btn" 
                @click="toggleProviders"
                :class="{ 'active': showProviders }"
              >
                <i :class="showProviders ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                {{ showProviders ? 'Hide' : 'Show' }} Provider Companies
              </button>
              <!-- Provider Listing -->
              <div v-if="showProviders" class="providers-container">
                <div class="providers-loading-overlay" v-if="isLoadingProviders">
                  <div class="spinner large"></div>
                </div>
                
                <!-- Unified Provider Display -->
                <template v-if="solution.solution_origin === 'vcg.ai'">
                  <VcgProviders
                    :providers="providerCompanies"
                    :user-lat="userCoordinates.lat"
                    :user-lon="userCoordinates.lon"
                  />
                </template>
                <template v-else>
                  <FoodcycleProviders
                    :providers="providerCompanies"
                    :user-lat="userCoordinates.lat"
                    :user-lon="userCoordinates.lon"
                  />
                </template>
              </div>
            </div>
          </div>
        </div>
        <!-- Action Buttons -->
        <div class="action-buttons">
          <button class="btn-primary" @click="goBack">
            <i class="bi bi-arrow-left"></i> Back to Solutions
          </button>
          <button class="btn-outline" @click="$router.push('/lca-stepper')">
            <i class="bi bi-bar-chart-line"></i> Proceed to Environmental Impact Report
          </button>
        </div>
      </div>
    </div>
    <Footer />
  </div>
</template>
<script>
import { ref, computed } from 'vue';
import { useStepperStore } from '@/stores/stepper';
import { useRouter } from 'vue-router';
import axios from 'axios';
import Header from '@/components/Header.vue';
import Footer from '@/components/Footer.vue';
import FoodcycleSolutionDetails from './templates/FoodcycleSolutionDetails.vue';
import VcgSolutionDetails from './templates/VcgSolutionDetails.vue';
import FoodcycleProviders from './providers/FoodcycleProviders.vue';
import VcgProviders from './providers/VcgProviders.vue';
export default {
  name: 'SolutionDetails',
  components: {
    Header,
    Footer,
    FoodcycleSolutionDetails,
    VcgSolutionDetails,
    FoodcycleProviders,
    VcgProviders
  },
  setup() {
    const stepperStore = useStepperStore();
    const router = useRouter();
    // Aktuell ausgewählte Solution
    const solution = stepperStore.selectedSolution;
    // CompanyProfile + WasteInfo aus dem Store
    const companyProfile = stepperStore.companyProfile;
    const wasteInfo = stepperStore.wasteInfo;
    const showProviders = ref(false);
    const providerCompanies = ref([]);
    const isLoadingProviders = ref(false);
    
    // Neu: Toggle-Zustand für Company & Surplus Information
    const showCompanyInfo = ref(false);
    // Lese Koordinaten des Surplus-Unternehmens
    // => companyProfile.location_geo.coordinates = [lon, lat]
    const userCoordinates = ref({ lat: null, lon: null });
    if (companyProfile.location_geo && companyProfile.location_geo.coordinates) {
      const coords = companyProfile.location_geo.coordinates; // [lon, lat]
      userCoordinates.value = {
        lat: coords[1],
        lon: coords[0]
      };
    }
    
    // Funktion zum Abrufen der effektiven Solution-ID
    function getEffectiveSolutionId(sol) {
      if (!sol) return null;
      
      // VCG.ai-Lösungen haben manchmal andere ID-Formate
      if (sol.solution_origin === 'vcg.ai') {
        return sol.id || sol.solution_id;
      }
      // FoodCycle-Lösungen
      return sol.solution_id || sol._id;
    }
    
    // Neu: Score und Reasoning aus dem Store holen
    const solutionScore = computed(() => {
      if (!solution) return null;
      
      // Debugging: Zeige alle relevanten Informationen
      console.log("Solution in Details:", solution);
      
      const solutionId = getEffectiveSolutionId(solution);
      console.log("Effective SolutionID:", solutionId);
      console.log("Solution Details in Store:", stepperStore.solutionDetails);
      
      return stepperStore.getSolutionScore(solutionId);
    });
    
    const solutionReasoning = computed(() => {
      if (!solution) return null;
      
      const solutionId = getEffectiveSolutionId(solution);
      return stepperStore.getSolutionReasoning(solutionId);
    });
    
    // Neue vereinfachte Helfer-Methoden für das schlichtere Score-Design
    function getScoreClass(score) {
      if (score >= 80) return 'score-high';
      if (score >= 50) return 'score-medium';
      return 'score-low';
    }
    
    function getScoreLabel(score) {
      if (score >= 90) return 'Excellent Match';
      if (score >= 80) return 'Very Good Match';
      if (score >= 70) return 'Good Match';
      if (score >= 60) return 'Fair Match';
      if (score >= 50) return 'Moderate Match';
      return 'Basic Match';
    }
    
    function formatAddress(loc) {
      if (!loc) return '';
      const addr = loc.address || '';
      const city = loc.city || '';
      const postal = loc.postalCode || loc.postal_code || '';
      const country = loc.country || '';
      return `${addr}, ${postal} ${city}, ${country}`.replace(/\s+,/g, '').trim();
    }
    
    function hasLocationInfo(loc) {
      if (!loc) return false;
      return !!(loc.address || loc.city || loc.postalCode || loc.postal_code || loc.country);
    }
    const frequencyString = computed(() => {
      const { times, interval } = wasteInfo.frequency;
      if (!times) return 'n/a';
      if (interval === 'once') {
        return times > 1 ? `${times} times` : '1 time';
      }
      return `${times} times per ${interval}`;
    });
    function goBack() {
      router.push({ name: 'solutions' });
    }
    
    function toggleCompanyInfo() {
      showCompanyInfo.value = !showCompanyInfo.value;
    }
    async function toggleProviders() {
      if (!showProviders.value) {
        // Erst beim "Show" die Provider laden (falls noch nicht geschehen)
        if (providerCompanies.value.length === 0 && solution) {
          isLoadingProviders.value = true;
          try {
            let url;
            if (solution.solution_origin === 'vcg.ai') {
              // vcg-Fall
              const vcgId = solution.id || solution.solution_id;
              url = `/provider_companies?solution_id=${vcgId}&solution_origin=vcg.ai`;
            } else {
              // foodcycle-Fall => lat/lon
              const solutionId = solution.solution_id || solution._id;
              url = `/provider_companies?solution_id=${solutionId}`;
              
              if (userCoordinates.value.lat !== null && userCoordinates.value.lon !== null) {
                url += `&lat=${userCoordinates.value.lat}&lon=${userCoordinates.value.lon}`;
              }
            }
            console.log("Fetching providers from URL:", url);
            const resp = await axios.get(url);
            console.log("Provider response:", resp.data);
            providerCompanies.value = resp.data.provider_companies || [];
          } catch (err) {
            console.error('Error fetching provider companies:', err);
            providerCompanies.value = [];
          } finally {
            isLoadingProviders.value = false;
          }
        }
      }
      showProviders.value = !showProviders.value;
    }
    return {
      solution,
      companyProfile,
      wasteInfo,
      showProviders,
      providerCompanies,
      isLoadingProviders,
      userCoordinates,
      showCompanyInfo,
      // Neu: Score und Reasoning
      solutionScore,
      solutionReasoning,
      
      // Neue Score-Helpers
      getScoreClass,
      getScoreLabel,
      formatAddress,
      hasLocationInfo,
      frequencyString,
      goBack,
      toggleProviders,
      toggleCompanyInfo
    };
  }
};
</script>
<style scoped>
.solution-details-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f9faf5;
}
.solution-details-container {
  flex: 1;
  padding: 2rem 1.5rem;
  max-width: 900px; /* Match stepper width */
  margin: 0 auto;
  width: 100%;
  margin-top: 80px;
}
/* Page header styling */
.page-header {
  text-align: center;
  margin-bottom: 2rem;
}
.page-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.5rem;
}
.page-subtitle {
  font-size: 1.2rem;
  color: #666;
  max-width: 700px;
  margin: 0 auto;
}
/* Breadcrumb navigation */
.breadcrumbs {
  margin-bottom: 2rem;
}
.breadcrumb-btn {
  background: none;
  border: none;
  color: #87b140;
  font-weight: 600;
  font-size: 1rem;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: color 0.2s ease;
}
.breadcrumb-btn:hover {
  color: #75a236;
  text-decoration: underline;
}
/* Outer Card Container (neu - wie in solutions.vue) */
.outer-card-container {
  background-color: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08), 0 5px 15px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(135, 177, 64, 0.1);
  position: relative;
  overflow: hidden;
  margin-bottom: 2rem;
}
.outer-card-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background-color: #87B140;
}
/* Content layout for vertical cards */
.content-layout {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-bottom: 2rem;
}
/* Collapse toggle styles */
.collapse-toggle {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 1.2rem;
  transition: all 0.2s ease;
}
.cursor-pointer {
  cursor: pointer;
}
/* Two-column Grid for larger screens */
.two-column-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}
@media (min-width: 768px) {
  .two-column-grid {
    grid-template-columns: 1fr 1fr;
  }
}
/* Card styling */
.detail-card {
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.3s ease;
  animation: fadeIn 0.5s ease;
  border: 1px solid #eee;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.detail-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}
.card-header {
  padding: 1.5rem;
  background-color: #f2f5e7; /* Grüner Hintergrund für den Header */
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.card-title {
  margin: 0;
  color: #333;
  font-size: 1.4rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.card-title i {
  color: #87b140;
  font-size: 1.2rem;
}
.card-body {
  padding: 1.5rem;
}
/* Section styling within cards */
.info-section {
  margin-bottom: 2rem;
}
.info-section:last-child {
  margin-bottom: 0;
}
.section-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #eee;
  position: relative;
}
.section-title::after {
  content: '';
  position: absolute;
  width: 60px;
  height: 3px;
  background-color: #87b140;
  bottom: -1px;
  left: 0;
  border-radius: 3px;
}
/* Info grid layout */
.info-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
}
@media (min-width: 576px) {
  .info-grid {
    grid-template-columns: 1fr 1fr;
  }
}
.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.info-item.full-width {
  grid-column: 1 / -1;
}
.info-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.info-value {
  font-size: 1rem;
  color: #333;
}
.empty-value {
  color: #999;
  font-style: italic;
}

/* Neues schlichteres Design für Match Score */
.match-score-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f8f9fa;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.match-score-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #555;
  margin-bottom: 0.5rem;
}

.match-score-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  margin-bottom: 0.5rem;
}

.match-score-bar {
  flex-grow: 1;
  height: 8px;
  background-color: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.match-score-fill {
  height: 100%;
  transition: width 0.5s ease;
  border-radius: 4px;
}

.match-score-fill.score-high {
  background-color: #4caf50;
}

.match-score-fill.score-medium {
  background-color: #ffc107;
}

.match-score-fill.score-low {
  background-color: #f44336;
}

.match-score-value {
  font-size: 1.1rem;
  font-weight: 700;
  min-width: 30px;
  text-align: right;
}

.match-score-value.score-high {
  color: #2e7d32;
}

.match-score-value.score-medium {
  color: #f57f17;
}

.match-score-value.score-low {
  color: #c62828;
}

.match-score-text {
  font-size: 0.8rem;
  color: #666;
}

/* Tags styling */
.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.tag {
  background-color: #f2f5e7;
  color: #558000;
  padding: 0.35rem 0.75rem;
  border-radius: 30px;
  font-size: 0.9rem;
  border: 1px solid rgba(135, 177, 64, 0.3);
}
/* Seasonal information styling */
.seasonal-info {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px dashed #ddd;
}
.subsection-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.monthly-capacity-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
}
.month-item {
  background-color: #f2f5e7;
  padding: 0.75rem;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.month-name {
  font-weight: 600;
  color: #333;
}
.month-value {
  font-size: 0.9rem;
  color: #558000;
}
/* Neu: Matching Reasoning Section */
.matching-reasoning-section {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
}
.reasoning-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}
.reasoning-header i {
  color: #87b140;
  font-size: 1.2rem;
}
.reasoning-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}
.reasoning-content {
  background-color: #f9faf5;
  padding: 1.25rem;
  border-radius: 8px;
  border-left: 3px solid #87b140;
}
.reasoning-content p {
  margin: 0;
  line-height: 1.6;
  color: #444;
}
/* Toggle providers button */
.toggle-providers-btn {
  background-color: #f2f5e7;
  color: #558000;
  border: 1px solid rgba(135, 177, 64, 0.3);
  border-radius: 6px;
  padding: 0.75rem 1.25rem;
  font-weight: 600;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 1.5rem;
}
.toggle-providers-btn:hover {
  background-color: rgba(135, 177, 64, 0.2);
}
.toggle-providers-btn.active {
  background-color: #87b140;
  color: white;
  border-color: #87b140;
}
/* Loading indicators */
.loading-indicator {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;
  color: #666;
}
.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(135, 177, 64, 0.3);
  border-top-color: #87b140;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
.spinner.large {
  width: 40px;
  height: 40px;
  border-width: 3px;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.providers-container {
  position: relative;
}
.providers-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}
/* Action buttons */
.action-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: wrap;
}
.btn-primary, .btn-outline {
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-primary {
  background-color: #87b140;
  color: white;
  border: none;
  box-shadow: 0 4px 10px rgba(135, 177, 64, 0.25);
}
.btn-primary:hover {
  background-color: #75a236;
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(135, 177, 64, 0.3);
}
.btn-outline {
  background-color: transparent;
  color: #87b140;
  border: 1px solid #87b140;
}
.btn-outline:hover {
  background-color: rgba(135, 177, 64, 0.1);
  transform: translateY(-2px);
}
/* Responsive adjustments */
@media (max-width: 768px) {
  .solution-details-container {
    padding: 1.5rem 1rem;
    margin-top: 60px;
  }
  
  .page-title {
    font-size: 2rem;
  }
  
  .page-subtitle {
    font-size: 1rem;
  }
  
  .outer-card-container {
    padding: 1.25rem;
  }
  
  .card-header {
    padding: 1.25rem;
  }
  
  .card-body {
    padding: 1.25rem;
  }
  
  .card-title {
    font-size: 1.2rem;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .monthly-capacity-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .btn-primary, .btn-outline {
    width: 100%;
    justify-content: center;
  }
}
@media (max-width: 576px) {
  .outer-card-container {
    padding: 1rem;
  }
  
  .card-header {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .solution-meta {
    align-self: flex-start;
    margin-top: 1rem;
    width: 100%;
  }
  
  .score-badge-container {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
  
  .score-gauge {
    margin-bottom: 0;
    width: 80px;
    height: 40px;
  }
  
  .card-body {
    padding: 1rem;
  }
}
</style>